/**
 * TreeViewControl
 * Collapsible tree renderer with node-level action buttons.
 *
 * Features:
 * - Recursive tree data normalised into flat DFS list for efficient rendering
 * - Expand/collapse with keyboard support (Enter/Space)
 * - Single and multi-select
 * - Per-node action buttons: url, form, event
 * - Per-node dynamic link from data fields
 * - Connector lines via left-border indentation
 * - Inline folder/file icons (or custom icon field)
 * - Max 500 rendered nodes with overflow warning
 * - Graceful handling of malformed data (no throws)
 * - All inline styles — zero external CSS dependencies
 */

import React, { memo, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';

// ---------------------------------------------------------------------------
// Config types
// ---------------------------------------------------------------------------

interface NodeAction {
  id: string;
  label: string;
  actionType: 'url' | 'form' | 'event';
  urlTemplate?: string;
  openIn?: 'same-tab' | 'new-tab';
  formId?: string;
  formIdField?: string;
  eventName?: string;
}

interface TreeViewConfig {
  data?: unknown;
  idField?: string;
  labelField?: string;
  descriptionField?: string;
  childrenField?: string;
  iconField?: string;
  showLines?: boolean;
  showIcons?: boolean;
  indentSize?: number;
  maxHeight?: string;
  defaultExpanded?: boolean | number;
  collapsible?: boolean;
  selectable?: boolean;
  multiSelect?: boolean;
  nodeActions?: NodeAction[];
  nodeLinkUrlField?: string;
  nodeLinkLabelField?: string;
  nodeLinkOpenIn?: 'same-tab' | 'new-tab';
}

// ---------------------------------------------------------------------------
// Internal tree node type
// ---------------------------------------------------------------------------

interface TreeNode {
  _id: string;
  _label: string;
  _description?: string;
  _icon?: string;
  _children: TreeNode[];
  _raw: Record<string, unknown>;
  _depth: number;
}

// ---------------------------------------------------------------------------
// Resolved config type (all optional fields replaced with concrete values)
// ---------------------------------------------------------------------------

type ResolvedConfig = {
  idField: string;
  labelField: string;
  descriptionField: string;
  childrenField: string;
  iconField: string | undefined;
  showLines: boolean;
  showIcons: boolean;
  indentSize: number;
  maxHeight: string | undefined;
  defaultExpanded: boolean | number;
  collapsible: boolean;
  selectable: boolean;
  multiSelect: boolean;
  // optional fields preserved as-is
  data: unknown;
  nodeActions: NodeAction[] | undefined;
  nodeLinkUrlField: string | undefined;
  nodeLinkLabelField: string | undefined;
  nodeLinkOpenIn: 'same-tab' | 'new-tab' | undefined;
};

// ---------------------------------------------------------------------------
// Helper: parseConfig
// ---------------------------------------------------------------------------

function parseConfig(config: Record<string, unknown> | undefined): ResolvedConfig {
  const c = (config ?? {}) as Partial<TreeViewConfig>;
  return {
    data: c.data,
    idField: typeof c.idField === 'string' && c.idField ? c.idField : 'id',
    labelField: typeof c.labelField === 'string' && c.labelField ? c.labelField : 'name',
    descriptionField:
      typeof c.descriptionField === 'string' && c.descriptionField
        ? c.descriptionField
        : 'description',
    childrenField:
      typeof c.childrenField === 'string' && c.childrenField ? c.childrenField : 'children',
    iconField: typeof c.iconField === 'string' && c.iconField ? c.iconField : undefined,
    showLines: typeof c.showLines === 'boolean' ? c.showLines : true,
    showIcons: typeof c.showIcons === 'boolean' ? c.showIcons : true,
    indentSize: typeof c.indentSize === 'number' && c.indentSize > 0 ? c.indentSize : 20,
    maxHeight: typeof c.maxHeight === 'string' && c.maxHeight ? c.maxHeight : undefined,
    defaultExpanded:
      typeof c.defaultExpanded === 'boolean' || typeof c.defaultExpanded === 'number'
        ? c.defaultExpanded
        : true,
    collapsible: typeof c.collapsible === 'boolean' ? c.collapsible : true,
    selectable: typeof c.selectable === 'boolean' ? c.selectable : false,
    multiSelect: typeof c.multiSelect === 'boolean' ? c.multiSelect : false,
    nodeActions: Array.isArray(c.nodeActions) ? (c.nodeActions as NodeAction[]) : undefined,
    nodeLinkUrlField:
      typeof c.nodeLinkUrlField === 'string' && c.nodeLinkUrlField
        ? c.nodeLinkUrlField
        : undefined,
    nodeLinkLabelField:
      typeof c.nodeLinkLabelField === 'string' && c.nodeLinkLabelField
        ? c.nodeLinkLabelField
        : undefined,
    nodeLinkOpenIn:
      c.nodeLinkOpenIn === 'same-tab' || c.nodeLinkOpenIn === 'new-tab'
        ? c.nodeLinkOpenIn
        : undefined,
  };
}

// ---------------------------------------------------------------------------
// Helper: buildTreeNodes
// ---------------------------------------------------------------------------

const MAX_DEPTH = 20;

function buildTreeNodes(
  raw: unknown,
  cfg: ResolvedConfig,
  depth: number = 0,
): TreeNode[] {
  if (depth > MAX_DEPTH) return [];
  if (raw == null) return [];

  try {
    // Normalise input to array
    const items: unknown[] = Array.isArray(raw) ? raw : [raw];

    const nodes: TreeNode[] = [];
    for (const item of items) {
      if (item == null || typeof item !== 'object') continue;

      const record = item as Record<string, unknown>;

      const rawId = record[cfg.idField];
      const _id = rawId != null ? String(rawId) : '';
      if (!_id) continue; // skip nodes without a valid id

      const rawLabel = record[cfg.labelField];
      const _label =
        rawLabel != null
          ? String(rawLabel)
          : _id; // fall back to id if no label

      const rawDesc = record[cfg.descriptionField];
      const _description = rawDesc != null ? String(rawDesc) : undefined;

      let _icon: string | undefined;
      if (cfg.iconField) {
        const rawIcon = record[cfg.iconField];
        if (rawIcon != null) _icon = String(rawIcon);
      }

      const rawChildren = record[cfg.childrenField];
      const _children =
        rawChildren != null && (Array.isArray(rawChildren) || typeof rawChildren === 'object')
          ? buildTreeNodes(rawChildren, cfg, depth + 1)
          : [];

      nodes.push({
        _id,
        _label,
        _description,
        _icon,
        _children,
        _raw: record,
        _depth: depth,
      });
    }
    return nodes;
  } catch (err) {
    console.warn('[TreeViewControl] buildTreeNodes error:', err);
    return [];
  }
}

// ---------------------------------------------------------------------------
// Helper: resolveTemplate
// ---------------------------------------------------------------------------

function resolveTemplate(template: string, node: TreeNode): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_match, fieldName: string) => {
    const val = node._raw[fieldName];
    return val != null ? String(val) : '';
  });
}

// ---------------------------------------------------------------------------
// Helper: getInitialExpanded
// ---------------------------------------------------------------------------

function collectAllIds(nodes: TreeNode[], result: Set<string>): void {
  for (const node of nodes) {
    result.add(node._id);
    if (node._children.length > 0) {
      collectAllIds(node._children, result);
    }
  }
}

function collectIdsByDepth(nodes: TreeNode[], maxDepth: number, result: Set<string>): void {
  for (const node of nodes) {
    if (node._depth < maxDepth) {
      result.add(node._id);
      if (node._children.length > 0) {
        collectIdsByDepth(node._children, maxDepth, result);
      }
    }
  }
}

function getInitialExpanded(nodes: TreeNode[], defaultExpanded: boolean | number): Set<string> {
  const result = new Set<string>();
  if (defaultExpanded === false) return result;
  if (defaultExpanded === true) {
    collectAllIds(nodes, result);
    return result;
  }
  // It's a number — expand nodes at depth < n
  const n = defaultExpanded as number;
  collectIdsByDepth(nodes, n, result);
  return result;
}

// ---------------------------------------------------------------------------
// Helper: handleNodeAction
// ---------------------------------------------------------------------------

function handleNodeAction(
  action: NodeAction,
  node: TreeNode,
  onNavigate?: (url: string, newTab: boolean) => void,
): void {
  try {
    if (action.actionType === 'url') {
      const template = action.urlTemplate ?? '';
      const url = resolveTemplate(template, node);
      if (!url) {
        console.warn('[TreeViewControl] url action has no urlTemplate or resolved to empty:', action);
        return;
      }
      const newTab = (action.openIn ?? 'new-tab') === 'new-tab';
      if (onNavigate) {
        onNavigate(url, newTab);
      } else if (newTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = url;
      }
    } else if (action.actionType === 'form') {
      let formId: string | undefined;
      if (action.formIdField) {
        const raw = node._raw[action.formIdField];
        formId = raw != null ? String(raw) : undefined;
      }
      if (!formId && action.formId) formId = action.formId;
      if (!formId) {
        console.warn('[TreeViewControl] form action has no formId:', action);
        return;
      }
      const evt = new CustomEvent('atlas-forms:open-form', {
        detail: { formId },
        bubbles: true,
        cancelable: false,
      });
      document.dispatchEvent(evt);
    } else if (action.actionType === 'event') {
      const eventName = action.eventName || 'atlas-forms:node-action';
      const evt = new CustomEvent(eventName, {
        detail: { nodeId: node._id, action },
        bubbles: true,
        cancelable: false,
      });
      document.dispatchEvent(evt);
    }
  } catch (err) {
    console.warn('[TreeViewControl] handleNodeAction error:', err);
  }
}

// ---------------------------------------------------------------------------
// Helper: flattenTree (DFS pre-order, skip collapsed subtrees)
// ---------------------------------------------------------------------------

function flattenTree(
  nodes: TreeNode[],
  expanded: Set<string>,
  result: TreeNode[] = [],
): TreeNode[] {
  for (const node of nodes) {
    result.push(node);
    if (node._children.length > 0 && expanded.has(node._id)) {
      flattenTree(node._children, expanded, result);
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Styles (inline)
// ---------------------------------------------------------------------------

const COLORS = {
  green: '#309B48',
  greenLight: 'rgba(48,155,72,0.12)',
  greenBorder: 'rgba(48,155,72,0.4)',
  text: '#101211',
  muted: '#6B7879',
  rowHover: 'rgba(0,0,0,0.04)',
  selectedBg: 'rgba(48,155,72,0.10)',
  connectorLine: '#d1d5db',
  warning: '#d97706',
  warningBg: '#fffbeb',
};

const baseRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 8px 4px 0',
  cursor: 'default',
  userSelect: 'none',
  borderRadius: 4,
  minHeight: 28,
  position: 'relative',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: COLORS.text,
  lineHeight: '1.4',
};

const descStyle: React.CSSProperties = {
  fontSize: 12,
  color: COLORS.muted,
  lineHeight: '1.4',
  marginLeft: 4,
};

const toggleButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '0 2px',
  fontSize: 10,
  color: COLORS.muted,
  lineHeight: '1',
  borderRadius: 3,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
  flexShrink: 0,
};

const actionButtonStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 400,
  padding: '2px 8px',
  borderRadius: 4,
  border: `1px solid ${COLORS.greenBorder}`,
  color: COLORS.green,
  background: 'transparent',
  cursor: 'pointer',
  lineHeight: '1.5',
  flexShrink: 0,
  transition: 'background 0.12s',
};

// ---------------------------------------------------------------------------
// TreeNodeRow sub-component
// ---------------------------------------------------------------------------

interface TreeNodeRowProps {
  node: TreeNode;
  isExpanded: boolean;
  isSelected: boolean;
  onToggle: (id: string) => void;
  onSelect: (id: string, multi: boolean) => void;
  cfg: ResolvedConfig;
}

const TreeNodeRow = memo(function TreeNodeRow({ node, isExpanded, isSelected, onToggle, onSelect, cfg }: TreeNodeRowProps) {
  const hasChildren = node._children.length > 0;
  const indentPx = cfg.indentSize * node._depth;

  // Row style — hover handled via onMouseEnter/Leave state
  const [hovered, setHovered] = useState(false);

  const rowStyle: React.CSSProperties = {
    ...baseRowStyle,
    paddingLeft: 8,
    background: isSelected
      ? COLORS.selectedBg
      : hovered
      ? COLORS.rowHover
      : 'transparent',
    cursor: cfg.selectable ? 'pointer' : 'default',
  };

  // Indent spacer — shows connector line via left-border when showLines is on
  const spacerStyle: React.CSSProperties = {
    display: 'inline-block',
    width: indentPx,
    flexShrink: 0,
    alignSelf: 'stretch',
    borderRight: cfg.showLines && indentPx > 0 ? `1px solid ${COLORS.connectorLine}` : undefined,
    marginRight: cfg.showLines && indentPx > 0 ? 4 : 0,
  };

  // Icon resolution
  let icon: string | null = null;
  if (cfg.showIcons) {
    if (cfg.iconField && node._icon) {
      icon = node._icon;
    } else if (hasChildren) {
      icon = isExpanded ? '📂' : '📁'; // 📂 / 📁
    } else {
      icon = '📄'; // 📄
    }
  }

  // Per-node dynamic link
  const nodeLinkUrl =
    cfg.nodeLinkUrlField && node._raw[cfg.nodeLinkUrlField]
      ? String(node._raw[cfg.nodeLinkUrlField])
      : null;

  const nodeLinkLabel =
    cfg.nodeLinkLabelField && node._raw[cfg.nodeLinkLabelField]
      ? String(node._raw[cfg.nodeLinkLabelField])
      : 'Open';

  const handleToggleClick = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      onToggle(node._id);
    },
    [onToggle, node._id],
  );

  const handleToggleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggleClick(e);
      }
    },
    [handleToggleClick],
  );

  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      if (!cfg.selectable) return;
      onSelect(node._id, e.metaKey || e.ctrlKey || e.shiftKey);
    },
    [cfg.selectable, onSelect, node._id],
  );

  return (
    <div
      style={rowStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleRowClick}
      role={cfg.selectable ? 'option' : undefined}
      aria-selected={cfg.selectable ? isSelected : undefined}
      data-node-id={node._id}
    >
      {/* Indent spacer with optional connector line */}
      {indentPx > 0 && <span style={spacerStyle} aria-hidden="true" />}

      {/* Toggle button */}
      {cfg.collapsible && hasChildren ? (
        <button
          type="button"
          style={toggleButtonStyle}
          onClick={handleToggleClick}
          onKeyDown={handleToggleKeyDown}
          aria-label={isExpanded ? `Collapse ${node._label}` : `Expand ${node._label}`}
          aria-expanded={isExpanded}
          tabIndex={0}
        >
          {isExpanded ? '▼' : '▶'}
        </button>
      ) : (
        /* spacer to keep alignment when no toggle */
        <span style={{ width: 16, flexShrink: 0 }} aria-hidden="true" />
      )}

      {/* Icon */}
      {icon && (
        <span style={{ fontSize: 14, flexShrink: 0, lineHeight: '1' }} aria-hidden="true">
          {icon}
        </span>
      )}

      {/* Label + description */}
      <span style={{ display: 'flex', alignItems: 'baseline', flex: 1, gap: 0, minWidth: 0, flexWrap: 'wrap' }}>
        <span style={labelStyle}>{node._label}</span>
        {node._description && (
          <>
            <span style={{ ...descStyle, marginLeft: 4 }} aria-hidden="true">
              {'—'}
            </span>
            <span style={descStyle}>{node._description}</span>
          </>
        )}
      </span>

      {/* Action buttons */}
      {cfg.nodeActions && cfg.nodeActions.length > 0 && (
        <span style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {cfg.nodeActions.map((action) => (
            <button
              key={action.id}
              type="button"
              style={actionButtonStyle}
              aria-label={`${action.label} for ${node._label}`}
              onClick={(e) => {
                e.stopPropagation();
                handleNodeAction(action, node);
              }}
            >
              {action.label}
            </button>
          ))}
        </span>
      )}

      {/* Per-node dynamic link button */}
      {nodeLinkUrl && (
        <a
          href={nodeLinkUrl}
          target={
            (cfg.nodeLinkOpenIn ?? 'new-tab') === 'new-tab' ? '_blank' : '_self'
          }
          rel="noopener noreferrer"
          style={{ ...actionButtonStyle, textDecoration: 'none', display: 'inline-block' }}
          aria-label={`${nodeLinkLabel} — ${node._label}`}
          onClick={(e) => e.stopPropagation()}
        >
          {nodeLinkLabel}
        </a>
      )}
    </div>
  );
});

TreeNodeRow.displayName = 'TreeNodeRow';

// ---------------------------------------------------------------------------
// TreeViewControl — main component
// ---------------------------------------------------------------------------

const MAX_RENDERED_NODES = 500;

export const TreeViewControl = memo((props: ControlRendererProps) => {
  const { control, value, mode } = props;

  // 1. Parse config
  const cfg = useMemo(
    () => parseConfig(control.config as Record<string, unknown> | undefined),
    [control.config],
  );

  // 2. Resolve data: value > config.data > control.defaultValue
  const rawData = value ?? cfg.data ?? control.defaultValue;

  // 3. Build tree nodes
  const rootNodes = useMemo(() => buildTreeNodes(rawData, cfg), [rawData, cfg]);

  // 4. Expanded state — initialised once; reset via useEffect when source data changes.
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    getInitialExpanded(rootNodes, cfg.defaultExpanded),
  );

  const prevRawDataRef = useRef(rawData);
  const prevDefaultExpandedRef = useRef(cfg.defaultExpanded);
  useEffect(() => {
    const dataChanged = prevRawDataRef.current !== rawData;
    const settingChanged = prevDefaultExpandedRef.current !== cfg.defaultExpanded;
    if (dataChanged || settingChanged) {
      prevRawDataRef.current = rawData;
      prevDefaultExpandedRef.current = cfg.defaultExpanded;
      setExpanded(getInitialExpanded(rootNodes, cfg.defaultExpanded));
    }
  }, [rawData, rootNodes, cfg.defaultExpanded]);

  // 5. Selected state
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Toggle expand/collapse
  const handleToggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Handle selection
  const handleSelect = useCallback(
    (id: string, multi: boolean) => {
      if (!cfg.selectable) return;
      setSelected((prev) => {
        if (cfg.multiSelect && multi) {
          const next = new Set(prev);
          if (next.has(id)) {
            next.delete(id);
          } else {
            next.add(id);
          }
          return next;
        }
        // Single select
        return prev.has(id) && prev.size === 1 ? new Set() : new Set([id]);
      });
    },
    [cfg.selectable, cfg.multiSelect],
  );

  // Flatten tree respecting expand state
  const flatNodes = useMemo(() => flattenTree(rootNodes, expanded), [rootNodes, expanded]);

  // Limit rendered nodes
  const exceededLimit = flatNodes.length > MAX_RENDERED_NODES;
  const visibleNodes = exceededLimit ? flatNodes.slice(0, MAX_RENDERED_NODES) : flatNodes;

  // Empty state
  const isEmpty = rootNodes.length === 0;

  // Container style — memoized so the object reference is stable when maxHeight hasn't changed.
  const containerStyle = useMemo((): React.CSSProperties => ({
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    ...(cfg.maxHeight
      ? { maxHeight: cfg.maxHeight, overflowY: 'auto', overflowX: 'hidden' }
      : {}),
  }), [cfg.maxHeight]);

  return (
    <div
      className="af-tree-view"
      data-control-id={control.id}
      data-control-type="tree-view"
      style={containerStyle}
    >
      {/* Label */}
      {control.label && (
        <div
          className="control__label"
          style={{ fontWeight: 600, fontSize: 13, color: COLORS.muted, marginBottom: 6 }}
        >
          {control.label}
        </div>
      )}

      {/* Empty state */}
      {isEmpty ? (
        <div
          style={{
            padding: '16px 12px',
            fontSize: 13,
            color: COLORS.muted,
            border: mode === 'design' ? '1px dashed #d1d5db' : undefined,
            borderRadius: mode === 'design' ? 4 : undefined,
            background: mode === 'design' ? '#f9fafb' : undefined,
          }}
        >
          {mode === 'design'
            ? 'Tree View — connect data via binding or set static data in properties'
            : 'No data to display'}
        </div>
      ) : (
        <div
          role={cfg.selectable ? 'listbox' : 'tree'}
          aria-label={control.label || 'Tree view'}
          aria-multiselectable={cfg.selectable && cfg.multiSelect ? true : undefined}
        >
          {visibleNodes.map((node) => (
            <TreeNodeRow
              key={node._id}
              node={node}
              isExpanded={expanded.has(node._id)}
              isSelected={selected.has(node._id)}
              onToggle={handleToggle}
              onSelect={handleSelect}
              cfg={cfg}
            />
          ))}

          {/* Overflow warning */}
          {exceededLimit && (
            <div
              style={{
                padding: '8px 12px',
                fontSize: 12,
                color: COLORS.warning,
                background: COLORS.warningBg,
                borderTop: `1px solid #fde68a`,
                marginTop: 4,
                borderRadius: 4,
              }}
            >
              {'⚠️'} Only the first {MAX_RENDERED_NODES} of {flatNodes.length} nodes are
              shown. Consider filtering data or increasing pagination.
            </div>
          )}
        </div>
      )}
    </div>
  );
});

TreeViewControl.displayName = 'TreeViewControl';

export { TreeViewControl as default };
